import { Alert, Badge, Breadcrumb, Button, Col, Dropdown, Modal, OverlayTrigger, Pagination, Row, Spinner, Table, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo, useState } from "react";
import LoggedInLayout from "../../layouts/loggedin-layout";
import { useErrorLogsQuery } from "../../new/errorlogs.query";
import styles from "../../styles/AuditLogs.module.css";

const pageSizeOptions = [10, 25, 50, 100];

const formatDateTime = (value) => {
    if (!value) {
        return "";
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "--";
    }

    return value;
};

const statusVariant = (statusCode) => {
    if (statusCode >= 500) {
        return "danger";
    }

    if (statusCode >= 400) {
        return "warning";
    }

    return "secondary";
};

const ErrorLogs = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [selectedLog, setSelectedLog] = useState(null);
    const queryParams = useMemo(() => ({
        page,
        pageSize
    }), [page, pageSize]);
    const errorLogsQuery = useErrorLogsQuery(queryParams);
    const data = errorLogsQuery.data;
    const items = data?.items ?? [];
    const totalCount = data?.totalCount ?? 0;
    const currentPage = data?.page ?? page;
    const currentPageSize = data?.pageSize ?? pageSize;
    const totalPages = Math.max(1, Math.ceil(totalCount / currentPageSize));
    const startItem = totalCount === 0 ? 0 : ((currentPage - 1) * currentPageSize) + 1;
    const endItem = Math.min(currentPage * currentPageSize, totalCount);

    useEffect(() => {
        document.title = "MFP - Error Logs";
    }, []);

    useEffect(() => {
        if (!errorLogsQuery.isFetching && totalCount > 0 && currentPage > totalPages) {
            setPage(totalPages);
        }
    }, [errorLogsQuery.isFetching, currentPage, totalCount, totalPages]);

    const handlePageSizeChange = (nextPageSize) => {
        setPageSize(nextPageSize);
        setPage(1);
    };

    return (
        <LoggedInLayout>
            <div style={{ padding: "25px" }}>
                <div className={styles.auditHeader}>
                    <div className={styles.auditTitleGroup}>
                        <h3>Error Logs</h3>
                        <Breadcrumb>
                            <Breadcrumb.Item className="primarybreadcrumb">Admin</Breadcrumb.Item>
                            <Breadcrumb.Item className="secondarybreadcrumb" active>Error Logs</Breadcrumb.Item>
                        </Breadcrumb>
                    </div>
                    <OverlayTrigger placement="bottom" overlay={<Tooltip>Refresh logs</Tooltip>}>
                        <Button
                            type="button"
                            variant="primary"
                            className={styles.detailsButton}
                            onClick={() => errorLogsQuery.refetch()}
                            disabled={errorLogsQuery.isFetching}
                            aria-label="Refresh logs"
                        >
                            {errorLogsQuery.isFetching ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <FontAwesomeIcon icon={faRotateRight} />
                            )}
                        </Button>
                    </OverlayTrigger>
                </div>

                <div className="seperator mb-3"></div>

                <div className={styles.toolbar}>
                    <span className={styles.pagingSummary}>
                        {startItem} - {endItem} of {totalCount}
                    </span>
                    <div className={styles.pageSizeControls}>
                        <span className={styles.pagingSummary}>Rows</span>
                        <Dropdown>
                            <Dropdown.Toggle className="dropdowncustom" id="error-page-size" disabled={errorLogsQuery.isFetching}>
                                {currentPageSize}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {pageSizeOptions.map((option) => (
                                    <Dropdown.Item
                                        as="button"
                                        key={option}
                                        type="button"
                                        onClick={() => handlePageSizeChange(option)}
                                    >
                                        {option}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>

                {errorLogsQuery.isError && (
                    <Alert variant="danger">
                        Error logs could not be loaded.
                    </Alert>
                )}

                {!errorLogsQuery.isError && errorLogsQuery.isLoading && (
                    <div className="d-flex align-items-center justify-content-center py-5">
                        <Spinner animation="border" className="me-2" />
                        <span>Loading</span>
                    </div>
                )}

                {!errorLogsQuery.isError && !errorLogsQuery.isLoading && items.length === 0 && (
                    <div className={styles.emptyState}>
                        No error logs found.
                    </div>
                )}

                {!errorLogsQuery.isError && items.length > 0 && (
                    <>
                        <div className={styles.tableWrap}>
                            <Table striped bordered hover responsive size="sm" className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Occurred</th>
                                        <th>Status</th>
                                        <th>Actor</th>
                                        <th>Request</th>
                                        <th>Exception</th>
                                        <th>Message</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item) => (
                                        <tr key={item.id}>
                                            <td className={styles.compactCell}>{formatDateTime(item.occurredUtc)}</td>
                                            <td>
                                                <Badge bg={statusVariant(item.statusCode)}>
                                                    {formatValue(item.statusCode)}
                                                </Badge>
                                            </td>
                                            <td>{formatValue(item.actorUsername)}</td>
                                            <td>
                                                <div>{formatValue(item.httpMethod)}</div>
                                                <small className={styles.mutedCell}>{formatValue(item.requestPath)}</small>
                                            </td>
                                            <td>{formatValue(item.exceptionType)}</td>
                                            <td>{formatValue(item.message)}</td>
                                            <td className="text-center">
                                                <OverlayTrigger placement="left" overlay={<Tooltip>View details</Tooltip>}>
                                                    <Button
                                                        type="button"
                                                        variant="primary"
                                                        className={styles.detailsButton}
                                                        onClick={() => setSelectedLog(item)}
                                                        aria-label="View error log details"
                                                    >
                                                        <FontAwesomeIcon icon={faCircleInfo} />
                                                    </Button>
                                                </OverlayTrigger>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        <Pagination className="justify-content-end mt-3">
                            <Pagination.Prev
                                onClick={() => setPage((current) => Math.max(current - 1, 1))}
                                disabled={currentPage === 1 || errorLogsQuery.isFetching}
                            />
                            <Pagination.Item active>{currentPage}</Pagination.Item>
                            <Pagination.Next
                                onClick={() => setPage((current) => Math.min(current + 1, totalPages))}
                                disabled={currentPage >= totalPages || errorLogsQuery.isFetching}
                            />
                        </Pagination>
                    </>
                )}
            </div>
            <ErrorLogDetailsModal log={selectedLog} onHide={() => setSelectedLog(null)} />
        </LoggedInLayout>
    );
};

const ErrorLogDetailsModal = ({ log, onHide }) => {
    if (!log) {
        return null;
    }

    return (
        <Modal show={Boolean(log)} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>Error Log Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col xs={12}>
                        <div className={styles.detailsGrid}>
                            <DetailItem label="Occurred" value={formatDateTime(log.occurredUtc)} />
                            <DetailItem label="Status" value={log.statusCode} />
                            <DetailItem label="Actor" value={log.actorUsername} />
                            <DetailItem label="Actor User ID" value={log.actorUserId} />
                            <DetailItem label="Exception Type" value={log.exceptionType} />
                            <DetailItem label="Message" value={log.message} />
                            <DetailItem label="Inner Exception Type" value={log.innerExceptionType} />
                            <DetailItem label="Inner Exception Message" value={log.innerExceptionMessage} />
                            <DetailItem label="Source" value={log.source} />
                            <DetailItem label="HTTP Method" value={log.httpMethod} />
                            <DetailItem label="Request Path" value={log.requestPath} />
                            <DetailItem label="Query String" value={log.queryString} />
                            <DetailItem label="Endpoint" value={log.endpoint} />
                            <DetailItem label="IP Address" value={log.ipAddress} />
                            <DetailItem label="Correlation ID" value={log.correlationId} />
                            <DetailItem label="Request ID" value={log.requestId} />
                            <DetailItem label="Trace ID" value={log.traceId} />
                            <DetailItem label="Session Hash" value={log.sessionIdHash} mono />
                            <DetailItem label="User Agent" value={log.userAgent} />
                        </div>
                    </Col>
                    <Col xs={12}>
                        <h6 className="fw-bold">Stack Trace</h6>
                        <pre className={styles.jsonBlock}>{formatValue(log.stackTrace)}</pre>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" variant="secondary" onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const DetailItem = ({ label, value, mono = false }) => (
    <div className={styles.detailItem}>
        <span className={styles.detailLabel}>{label}</span>
        <span className={`${styles.detailValue} ${mono ? styles.hashText : ""}`}>
            {formatValue(value)}
        </span>
    </div>
);

export default ErrorLogs;
